// Manage saved recurring rules independently of generated tasks.
import { useEffect, useRef, useState } from "react";
import UIkit from "uikit";
import TaskRecurrenceRuleService from "../../../services/taskRecurrenceRule.service";
import TaskOccurrencesService from "../../../services/taskOccurrences.service";
import { TASK_TYPE_LABELS } from "../taskTypeOptions";
import FeatherIcon from "../../common/FeatherIcon";

const describeRule = (rule) => {
  if (!rule) return "";
  if (rule.mode === "anchor") {
    const trigger = rule.anchor_type === "checkout_plus" ? "after checkout" : "before check-in";
    return `${trigger}, +${rule.anchor_offset_days || 0}d at ${rule.time_of_day || ""}`;
  }
  let parts = [];
  parts.push(rule.frequency);
  if (rule.frequency === "weekly" && rule.weekdays?.length) {
    const dn = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
    parts.push(rule.weekdays.map((d) => dn[d]).join("/"));
  }
  if (rule.month_day) parts.push(`day ${rule.month_day}`);
  parts.push(`at ${rule.time_of_day || ""}`);
  return parts.join(" ");
};

const RecurrenceRulesPanel = ({ open, onClose, onChanged, onEdit, canEdit = false }) => {
  const [rules, setRules] = useState([]);
  const [loading, setLoading] = useState(false);
  const [pending, setPending] = useState(false);
  const pendingRef = useRef(false);
  const [deleteRule, setDeleteRule] = useState(null);
  const confirmationRef = useRef(null);
  useEffect(() => {
    if (deleteRule) {
      confirmationRef.current?.focus();
      confirmationRef.current?.scrollIntoView?.({ block: "nearest" });
    }
  }, [deleteRule]);
  const [actionError, setActionError] = useState("");
  useEffect(() => { if (!canEdit || !open) setDeleteRule(null); }, [canEdit, open]);
  const changeRule = async (action) => {
    if (!canEdit || pendingRef.current) return;
    pendingRef.current = true;
    setPending(true);
    setActionError("");
    try { await action(); }
    catch (error) {
      const message = error?.response?.data?.error;
      setActionError(typeof message === "string" ? message : error.message || "Unable to update the recurring task. Try again.");
    } finally {
      pendingRef.current = false;
      setPending(false);
    }
  };

  const load = async () => {
    setLoading(true);
    try {
      const res = await TaskRecurrenceRuleService.list({});
      setRules(res?.data?.rules || res?.data || []);
    } catch (e) {
      console.error("Failed to load rules", e);
      UIkit.notification("Failed to load recurring rules", "danger");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (open) load();
  }, [open]);

  const preview = async (rule) => {
    try {
      const today = new Date();
      const dateFrom = today.toISOString().slice(0, 10);
      const dateTo = new Date(today.getTime() + 30 * 86400000).toISOString().slice(0, 10);
      const res = await TaskOccurrencesService.list({
        date_from: dateFrom,
        date_to: dateTo,
        rule_id: rule.id,
      });
      const occs = (res?.data?.occurrences || []).filter((o) => o.rule_id === rule.id);
      const lines = occs.slice(0, 15).map(
        (o) => `${o.scheduled_date} · ${o.summary || "Task"}${o.kind === "virtual" ? " (planned)" : ""}`
      );
      const msg = lines.length
        ? `Next ${lines.length} occurrence(s):\n${lines.join("\n")}`
        : "No occurrences in the next 30 days.";
      UIkit.notification(msg, { status: "primary", timeout: 8000 });
    } catch (e) {
      console.error("preview failed", e);
      UIkit.notification("Preview failed", "danger");
    }
  };

  const remove = () => changeRule(async () => {
    if (!deleteRule) return;
    await TaskRecurrenceRuleService.remove(deleteRule.id);
    setRules((previous) => previous.filter((r) => String(r.id) !== String(deleteRule.id)));
    setDeleteRule(null);
    onChanged?.();
  });

  if (!open) return null;

  return (
    <div
      className="hms-side-drawer"
      role="complementary"
      aria-label="Recurring Tasks"
      style={{
        position: "fixed", top: 0, right: 0, bottom: 0, width: 480,
        maxWidth: "100%", background: "#fff", boxShadow: "-4px 0 16px rgba(0,0,0,.08)",
        zIndex: 1010, display: "flex", flexDirection: "column",
      }}
    >
      <div className="hms-modals" style={{ position: "relative", flexShrink: 0 }}>
        <button
          className="uk-offcanvas-close uk-close-large"
          type="button"
          aria-label="Close recurring tasks"
          onClick={onClose}
          data-uk-close
        />
        <div className="uk-modal-header">
          <h2 className="uk-modal-title">Recurring Tasks</h2>
        </div>
      </div>

      <div style={{ overflowY: "auto", padding: 16, flex: 1 }}>
        <p className="uk-text-meta">Saved recurring rules appear here even when no tasks are due. The task list shows generated tasks for eligible dates and guest stays.</p>
        {actionError && <p role="alert" className="uk-text-danger">{actionError}</p>}
        {canEdit && deleteRule && <div ref={confirmationRef} tabIndex={-1} role="alertdialog" aria-modal="false" aria-labelledby="delete-rule-title" className="uk-card uk-card-default uk-card-body uk-margin-small-bottom">
          <h4 id="delete-rule-title">Delete recurring task?</h4>
          <p>Delete “{deleteRule.name || deleteRule.summary_template}”? This removes the rule and stops future task generation. Existing tasks, including already scheduled tasks, will remain.</p>
          <button type="button" className="uk-button hms-btn hms-red-btn" disabled={pending} onClick={remove}>Delete recurring task</button>
          <button type="button" className="uk-button hms-btn" disabled={pending} onClick={() => setDeleteRule(null)}>Cancel</button>
        </div>}
        {loading && <div className="uk-text-meta">Loading…</div>}
        {!loading && rules.length === 0 && (
          <div className="uk-text-meta">No recurring rules yet. Use the Add Task form and choose "Recurring" to create one.</div>
        )}
        {rules.map((rule) => (
          <div
            key={rule.id}
            className="uk-card uk-card-default uk-card-small uk-card-body uk-margin-small-bottom"
          >
            <div className="uk-flex uk-flex-between uk-flex-middle">
              <div style={{ flex: 1 }}>
                <div style={{ fontWeight: 600 }}>{rule.name || rule.summary_template}</div>
                <div className="uk-text-meta">
                  {TASK_TYPE_LABELS[rule.task_type] || rule.task_type} · {describeRule(rule)}
                </div>
              </div>
              <div>
                {rule.is_active
                  ? <span className="uk-label uk-label-success">Active</span>
                  : <span className="uk-label">Inactive</span>}
              </div>
            </div>
            <div className="uk-margin-small-top">
              <button
                type="button"
                className="uk-button uk-button-small uk-button-default uk-margin-small-right"
                onClick={() => preview(rule)}
              >
                Preview 30 days
              </button>
              {canEdit && onEdit && <button type="button" className="uk-button hms-simple-icon-btn uk-margin-small-right" title="Edit" uk-tooltip="Edit" aria-label={`Edit ${rule.name || rule.summary_template}`} disabled={pending} onClick={() => changeRule(() => onEdit(rule))}><FeatherIcon name="edit-3" /></button>}
              {canEdit && <button type="button" className="uk-button hms-simple-icon-btn uk-margin-small-right" title="Delete" uk-tooltip="Delete" aria-label={`Delete ${rule.name || rule.summary_template}`} disabled={pending} onClick={() => { setActionError(""); setDeleteRule(rule); }}><FeatherIcon name="trash-2" /></button>}

            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default RecurrenceRulesPanel;
